import type StoreService from '@ember-data/store';

interface TrackingAdapter {
  host: string;
  headers: Record<string, string>;
}

export interface ReportCompanyViews {
  company_id: number | null;
  company_name: string;
  views: number;
}

export interface ReportStats {
  total_views: number;
  unique_users: number;
  export_count: number;
  filters_used: boolean;
  sorts_used: boolean;
  views_by_company: ReportCompanyViews[];
}

/**
 * Fetch aggregated stats for a single report from the per-report stats endpoint.
 * When `companyId` is provided, the stats are scoped to that company's viewers
 * (used by the partner listing). Returns null on any failure so callers can show
 * an error/empty state.
 */
export async function fetchReportStats(
  store: StoreService,
  reportId: string,
  companyId?: string
): Promise<ReportStats | null> {
  if (!reportId) return null;

  const adapter = store.adapterFor?.('report-tracking') as TrackingAdapter | undefined;
  if (!adapter?.host) return null;

  const params = new URLSearchParams();
  params.set('filter[report_id]', reportId);
  if (companyId) params.set('filter[company_id]', companyId);

  const url = `${adapter.host}/api/v2/report-trackings/stats?${params.toString()}`;

  try {
    const response = await fetch(url, {
      headers: {
        ...adapter.headers,
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;

    const json = (await response.json()) as { data?: ReportStats };
    return json.data ?? null;
  } catch {
    return null;
  }
}
